import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  LocationDeleteImpact,
  SettingsService,
} from '../../core/services/settings.service';
import { Location } from '../../core/models/settings.models';

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './locations.component.html',
  styleUrls: ['../../settings-shared.css', './locations.component.css'],
})
export class LocationsComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);

  protected readonly showDeleteModal = signal(false);
  protected readonly deleteLoading = signal(false);
  protected readonly deleteSubmitting = signal(false);
  protected readonly deleteTarget = signal<Location | null>(null);
  protected readonly deleteImpact = signal<LocationDeleteImpact | null>(null);
  protected readonly deleteError = signal('');
  protected targetStoreLocationId = '';

  protected locationForm: Omit<Location, 'id'> = {
    code: '',
    name: '',
    branchId: '',
    type: 'warehouse',
    status: 'active',
  };

  protected readonly locations = this.settings.companyLocations;
  protected readonly branches = this.settings.companyBranches;

  protected readonly branchNameById = computed(() => {
    const map = new Map<string, string>();
    for (const branch of this.branches()) {
      map.set(branch.id, branch.name);
    }
    return map;
  });

  protected readonly destinationLocations = computed(() => {
    const target = this.deleteTarget();
    if (!target) {
      return [] as Location[];
    }
    return this.locations().filter(
      (loc) =>
        loc.id !== target.id &&
        loc.branchId === target.branchId &&
        loc.status === 'active',
    );
  });

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Location): void {
    this.editingId.set(row.id);
    this.locationForm = {
      code: row.code,
      name: row.name,
      branchId: row.branchId,
      type: row.type,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  getBranchName(branchId: string): string {
    return this.branchNameById().get(branchId) ?? '—';
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.locationForm.name?.trim()) {
      this.showNotice('Name is required', true);
      return;
    }

    if (!this.locationForm.branchId) {
      this.showNotice('Please select a branch', true);
      return;
    }

    if (editingId) {
      this.settings.updateLocation(editingId, this.locationForm).subscribe((result) => {
        this.showNotice(result.message, !result.success);
        if (result.success) {
          this.closeModal();
        }
      });
      return;
    }

    this.settings.addLocation(this.locationForm);
    this.closeModal();
    this.showNotice('Location added successfully');
  }

  deleteRow(row: Location): void {
    this.deleteTarget.set(row);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetStoreLocationId = '';
    this.showDeleteModal.set(true);
    this.deleteLoading.set(true);

    this.settings.getLocationDeleteImpact(row.id).subscribe((impact) => {
      this.deleteLoading.set(false);
      if (!impact) {
        this.deleteError.set('Could not check items in this location. Try again.');
        return;
      }
      this.deleteImpact.set(impact);
      const destinations = this.destinationLocations();
      if (impact.requiresReassignment && destinations.length === 1) {
        this.targetStoreLocationId = destinations[0].id;
      }
    });
  }

  closeDeleteModal(): void {
    if (this.deleteSubmitting()) {
      return;
    }
    this.showDeleteModal.set(false);
    this.deleteTarget.set(null);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetStoreLocationId = '';
  }

  confirmDelete(): void {
    const target = this.deleteTarget();
    const impact = this.deleteImpact();
    if (!target || !impact || this.deleteSubmitting()) {
      return;
    }

    if (impact.requiresReassignment) {
      const destId = Number(this.targetStoreLocationId);
      if (!destId) {
        this.deleteError.set(
          'Select where stock and items from this location should go.',
        );
        return;
      }
    }

    this.deleteError.set('');
    this.deleteSubmitting.set(true);

    const options = impact.requiresReassignment
      ? { targetStoreLocationId: Number(this.targetStoreLocationId) }
      : undefined;

    this.settings.deleteLocation(target.id, options).subscribe((result) => {
      this.deleteSubmitting.set(false);
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeDeleteModal();
      } else {
        this.deleteError.set(result.message);
      }
    });
  }

  private resetForm(): void {
    this.locationForm = {
      code: '',
      name: '',
      branchId: '',
      type: 'warehouse',
      status: 'active',
    };
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  getTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      warehouse: 'bi-building',
      store: 'bi-shop',
      garage: 'bi-truck',
      office: 'bi-briefcase',
    };
    return icons[type] || 'bi-building';
  }
}
